import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { KaabaIcon } from '../animations/KaabaIcon';

// The 3D Kaaba needs three.js (~700 KB, ~1.4 s of script on a mid-range phone).
// Load it only after the visitor first interacts (scroll, tap, key, mouse move),
// and show the flat Kaaba until then, so it never weighs on the initial load.
const Kaaba3D = lazy(() => import('../animations/Kaaba3D'));

const INTERACTION_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart', 'wheel'];

const useAfterInteraction = () => {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const go = () => setReady(true);
        INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, go, { once: true, passive: true }));
        return () => INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, go));
    }, []);
    return ready;
};

const Logo = ({ variant = 'default', size = 'medium' }) => {
    const theme = useTheme();
    const show3D = useAfterInteraction();
    
    // Size mappings
    const sizeMap = {
        small: { icon: 24, text: 'body1' },
        medium: { icon: 36, text: 'h6' },
        large: { icon: 48, text: 'h4' }
    };
    
    // Color scheme based on variant
    const getColors = () => {
        switch(variant) {
            case 'light':
                return {
                    text: 'white',
                    gradient: `linear-gradient(90deg, white, ${theme.palette.secondary.light})`
                };
            case 'dark':
                return {
                    text: theme.palette.primary.main,
                    gradient: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.dark})`
                };
            default:
                return {
                    text: 'white',
                    gradient: `linear-gradient(90deg, white, ${theme.palette.secondary.light})`
                };
        }
    };
    
    const colors = getColors();
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Kaaba3D logo */}
            <Box
                sx={{
                    width: sizeMap[size].icon,
                    height: sizeMap[size].icon,
                    mr: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    top: 7 // Move Kaaba3D a little down (adjust value as needed)
                }}
                role="img"
                aria-label="iMuslim Life Logo Kaaba"
            >
                {show3D ? (
                    <Suspense fallback={<KaabaIcon size={40} />}>
                        <Kaaba3D />
                    </Suspense>
                ) : (
                    <KaabaIcon size={40} />
                )}
            </Box>
            <Typography 
                variant={sizeMap[size].text}
                component="div" 
                sx={{ 
                    fontWeight: 600,
                    background: colors.gradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }}
            >
                <Box component="span" sx={{ fontWeight: 700 }}>
                    i
                </Box>
                Muslim Life
            </Typography>
        </Box>
    );
};

export default Logo;